import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { action } from '@ember/object';
import { fn, hash } from '@ember/helper';
import { indexInRange } from '../../../../utils/spds/array-handling';
import { reads } from 'macro-decorators';
import { waitForRender } from '../../../../utils/waiters';
import Checkbox from '../checkbox.gjs';
import GroupLayout from './layout.gjs';
import Super from '../../core/menu/parent.gjs';
import styles from './checkbox.module.scss';

const SELECT_ALL_CLASS = 'select-all';

const METADATA = Metadata.define({
  description: `Selection controls for choosing from predefined options.

### Required arguments
The \`@groupName\` and \`@onClick\` arguments are always required.
Use \`@checkboxData\` or the \`:inputs\` named block to define options.

#### \`@groupName\`
The \`@groupName\` argument is required to properly group together the checkboxes within the group.

#### \`@onClick\`
The \`@onClick\` action is required to get the state of the checkboxes on user interaction.
It receives an object with attributes named \`event\`, \`groupName\`, and \`values\` (array of checked values).

#### \`@checkboxData\`
The \`@checkboxData\` argument must be an array of objects with \`checked\`, \`label\`, and \`value\` for each option.
When using \`@checkboxData\`, element IDs are computed as \`#\${groupName}-\${groupId}-\${inputIndex}\` and \`#\${groupName}-\${groupId}-\${inputIndex}-label\`.

### Contextual components
The checkbox is passed into the group using the \`checkbox\` key. A "Select All" checkbox is available using the \`selectAll\` key.

### Event handling
The \`@onBlur\`, \`@onChange\`, and \`@onFocus\` actions are optional and are forwarded uniformly to every checkbox rendered within the group, regardless of whether \`@checkboxData\`, contextual components, or the \`:inputs\` named block is used.
Each action receives an object with attributes named \`checked\`, \`event\`, \`groupName\`, \`id\`, and \`value\`, matching the format of \`@onClick\`.`,
  extends: [
    pick(ACTION_ARGS, ['onBlur', 'onChange', 'onFocus', { name: 'onClick', required: true }]),
    pick(COMPONENT_STATE_ARGS, ['disabled']),
    pick(INPUT_GROUP_ARGS, [
      { name: 'description', initialValue: 'Checkbox group description' },
      { name: 'hideDescription', defaultValue: false },
      { name: 'layout', defaultValue: 'column' },
      { name: 'groupName', initialValue: 'checkbox-group' },
    ]),
    pick(INPUT_FORM_ARGS, ['required']),
  ],
  meta: {
    checkboxData: Arg.array({
      description: 'Array of { label, value, checked } for each option',
    }),
    displayedItemCount: Arg.number({ description: 'Number of displayed items' }),
    onSetup: Arg.action({ description: 'Called when initial selected values are available' }),
    selectAll: Arg.action({ description: 'Custom select-all handler' }),
  },
  name: 'Group Checkbox',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=15794-125753&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/007916-checkbox',
  },
});

export default class SpdsInputGroupCheckbox extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  elementName = 'inputElement';
  baseClass = 'spds-input-checkbox-group';
  selectAllClass = SELECT_ALL_CLASS;
  styles = styles;
  type = 'checkbox';

  @reads('filteredNavigableOptions.length', 0) optionCount;
  @reads('selected.length', 0) selectedCount;

  get allSelected() {
    return this.selectedCount === this.optionCount;
  }

  get filteredNavigableOptions() {
    // filter out select all option from count
    return this.navigableOptions.filter(option => !option?.classList?.contains(SELECT_ALL_CLASS));
  }

  get hideDescription() {
    return this.argOrDefault('hideDescription');
  }

  get layout() {
    return this.argOrDefault('layout');
  }

  get selected() {
    let selected = [];

    this.filteredNavigableOptions.forEach(({ checked, classList, name: groupName, id, value }) => {
      if (checked && !classList.contains(SELECT_ALL_CLASS)) selected.push({ id, groupName, value });
    });

    return selected;
  }

  get someSelected() {
    return indexInRange(this.optionCount, this.selectedCount);
  }

  constructor() {
    super(...arguments);

    this.#setupCheckedBoxData();
  }

  async #setupCheckedBoxData() {
    let { groupName } = this.args;

    await waitForRender();

    if (this.selected.length > 0) {
      this.args.onSetup?.({ groupName, values: this.selected.map(option => option.value) });
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);

    this.baseElement.removeEventListener('clear', this.clear);
    this.baseElement.removeEventListener('sync', this.updateItems);
  }

  clear = () => {
    this.filteredNavigableOptions.forEach(element => (element.checked = false));

    this.updateItems();

    if (this.selectAllComponent) {
      this.selectAllComponent.inputElement.checked = false;
    }
  };

  setup = () => {
    this.baseElement.addEventListener('clear', this.clear);
    this.baseElement.addEventListener('sync', this.updateItems);
  };

  onClick = ({ checked, event, groupName, id }) => {
    let index = this.navigableOptions.findIndex(element => element.id === id);

    if (this.navigableOptions[index]) this.navigableOptions[index].checked = checked;

    this.updateItems();

    this.args.onClick({
      event,
      groupName,
      values: this.selected.map(element => element.value),
    });
  };

  @action
  selectAll({ event, groupName }) {
    if (this.args.selectAll) {
      this.args.selectAll(...arguments);
      return;
    }

    let { checked } = event.target;

    this.filteredNavigableOptions.forEach(option => (option.checked = checked));

    this.updateItems();

    if (!checked) this.clear();

    this.args.onClick({
      event,
      groupName,
      values: checked ? this.selected.map(option => option.value) : [],
    });
  }

  <template>
    <GroupLayout
      @component={{this.constructor.metadata.component}}
      @disabled={{@disabled}}
      @groupName={{@groupName}}
      @hideDescription={{this.hideDescription}}
      @id={{this.id}}
      @layout={{this.layout}}
      @onBlur={{@onBlur}}
      @onChange={{@onChange}}
      @onClick={{this.onClick}}
      @onFocus={{@onFocus}}
      @required={{@required}}
      class={{this.classes}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      <:description>
        {{#if (has-block "description")}}
          {{yield to="description"}}
        {{else}}
          {{@description}}
        {{/if}}
      </:description>
      <:inputs as |group|>
        {{#if @checkboxData}}
          {{#each @checkboxData as |checkbox index|}}
            <Checkbox
              @checked={{checkbox.checked}}
              @disabled={{@disabled}}
              @id="{{group.id}}-{{index}}"
              @label={{checkbox.label}}
              @groupName={{group.groupName}}
              @onBlur={{group.onBlur}}
              @onChange={{group.onChange}}
              @onClick={{group.onClick}}
              @onFocus={{group.onFocus}}
              @registerComponent={{fn this.registerItem false}}
              @unregisterComponent={{this.unregisterItem}}
              @value={{checkbox.value}}
            />
          {{/each}}
        {{else}}
          {{yield
            (hash
              optionCount=this.optionCount
              checkbox=(component
                Checkbox
                disabled=@disabled
                groupName=group.groupName
                onBlur=group.onBlur
                onChange=group.onChange
                onClick=group.onClick
                onFocus=group.onFocus
                registerComponent=(fn this.registerItem false)
                unregisterComponent=this.unregisterItem
              )
              selectAll=(component
                Checkbox
                checked=this.allSelected
                disabled=@disabled
                groupName=group.groupName
                indeterminate=this.someSelected
                onBlur=group.onBlur
                onChange=group.onChange
                onClick=this.selectAll
                onFocus=group.onFocus
                registerComponent=(fn this.registerItem true)
                unregisterComponent=this.unregisterItem
              )
            )
          }}
        {{/if}}
      </:inputs>
    </GroupLayout>
  </template>
}

export const INPUT_GROUP_CHECKBOX_METADATA = SpdsInputGroupCheckbox.metadata;
